import { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, Pane, useMap } from 'react-leaflet';
import { useParams, useSearchParams } from 'react-router-dom';
import postal from 'postal';
import ReactLeafletKml from 'react-leaflet-kml';
import useApi from '../../../utilities/useApi';
import arrayUtilities from '../../../utilities/arrayUtilities';
import stringUtilities from '../../../utilities/stringUtilities';
import geospatialUtilities from '../../../utilities/geospatialUtilities';
import snapUtilities from '../../../utilities/snapUtilities';
import Basemap from '../basemap';
import DataPoint from '../dataPoint';
import BufferOrdering from '../bufferOrdering';
import { BUFFER_PANE, BUFFER_PANE_Z_INDEX } from '../../../utilities/constants';
import DataGeomtry from '../dataGeometry';
import Sketch from '../sketch';
import { Surface } from '../surface';

// Inner component — has access to the Leaflet map instance via useMap.
function RenderController({ sketch, limitIndex, workflowId, tileLayerRef, mapOutputWidth, mapOutputHeight, outputFormat, workflowLines, workflowPoints, workflowPointsLines, points, lines, snappingTolerance, onDimensionsDetected, onMapPositioned }) {
    const map = useMap();
    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (!sketch || hasFiredRef.current) {
            return;
        }

        const limit = sketch?.limits?.[limitIndex];
        if (!limit || arrayUtilities.isNullOrEmpty(limit.limitOfSketchCoords)) {
            return;
        }

        // If a specific workflow is requested but it has no items configured in this sketch, skip rendering.
        if (workflowId !== 'ALL') {
            const sketchWorkflowIds = [...(workflowLines ?? []), ...(workflowPoints ?? []), ...(workflowPointsLines ?? [])];
            if (!sketchWorkflowIds.some(id => id?.toString().toLowerCase() === workflowId.toLowerCase())) {
                document.body.dataset.renderEmpty = 'true';
                document.body.dataset.renderComplete = 'true';
                return;
            }
        }

        let p0 = map.project(limit.limitOfSketchCoords[0], 10);
        let p1 = map.project(limit.limitOfSketchCoords[1], 10);

        let xDiff = Math.max(p0.x, p1.x) - Math.min(p0.x, p1.x);
        let yDiff = Math.max(p0.y, p1.y) - Math.min(p0.y, p1.y);

        let losRatio = yDiff / xDiff;
        let losRadioIsLandscape = losRatio > 1;

        let effectiveWidth = losRadioIsLandscape ? Math.min(mapOutputWidth, mapOutputHeight) : Math.max(mapOutputWidth, mapOutputHeight);
        let effectiveHeight = losRadioIsLandscape ? Math.max(mapOutputWidth, mapOutputHeight) : Math.min(mapOutputWidth, mapOutputHeight);
        onDimensionsDetected(effectiveWidth, effectiveHeight);

        const mapContainer = document.querySelector('.map-container');
        if (mapContainer) {
            mapContainer.classList.add('is-printing');
            if (outputFormat === 1) {
                mapContainer.classList.add('is-printing-png');
                document.body.style.background = 'transparent';
            }
        }

        // AbortController allows the Strict Mode cleanup to cancel the pending timer so only
        // the final real mount actually fires the render logic.
        const abortController = new AbortController();
        const { signal } = abortController;

        // Fit map to the limit bounds.
        const timerId = setTimeout(async () => {
            if (signal.aborted) return;

            hasFiredRef.current = true;

            map.invalidateSize();

            const limitCenter = {
                lat: (limit.limitOfSketchCoords[0].lat + limit.limitOfSketchCoords[1].lat) / 2,
                lng: (limit.limitOfSketchCoords[0].lng + limit.limitOfSketchCoords[1].lng) / 2,
            };
            const savedZoom = sketch.configuration?.zoom ?? limit.configuration?.zoom;
            if (savedZoom > 0) {
                map.setView(limitCenter, savedZoom, { animate: false });
            } else {
                const shifted = geospatialUtilities.coordsToLatLngArray([
                    geospatialUtilities.shiftPoint(limit.limitOfSketchCoords[0], -1, -1),
                    geospatialUtilities.shiftPoint(limit.limitOfSketchCoords[1], 1, 1)
                ]);
                map.fitBounds(shifted, { animate: false, padding: [0, 0] });
            }

            onMapPositioned();

            await new Promise(resolve => setTimeout(resolve, 200));
            if (signal.aborted) return;

            const computeNormallyVisibleWorkflowIds = function (workflowId) {
                if (stringUtilities.isNullOrEmpty(workflowId) || workflowId === 'ALL') return null;

                let dimensionLines = sketch?.items?.filter(x => x.tool?.id === 1);
                if (arrayUtilities.isNullOrEmpty(dimensionLines)) return [];

                let connected = new Set();

                for (let i = 0; i < dimensionLines.length; i++) {
                    if (!arrayUtilities.isNullOrEmpty(dimensionLines[i].coords)) {
                        let lineWorkflowIds = [];
                        for (let j = 0; j < dimensionLines[i].coords.length; j++) {
                            let snapResult = snapUtilities.snapToNearest(dimensionLines[i].coords[j], snappingTolerance, points, lines, sketch, false, workflowLines, workflowPoints, workflowPointsLines);
                            if (snapResult && !stringUtilities.isNullOrEmpty(snapResult.workflowId)) {
                                lineWorkflowIds.push(snapResult.workflowId);
                            }
                        }
                        if (lineWorkflowIds.includes(workflowId)) {
                            lineWorkflowIds.forEach(id => connected.add(id));
                        }
                    }
                }

                return [...connected];
            };

            postal.publish({
                channel: 'map',
                topic: 'printing',
                data: { sketchLimitIndex: limitIndex, workflowId: workflowId, normallyVisibleWorkflowIds: computeNormallyVisibleWorkflowIds(workflowId) }
            });

            // Poll tile layer until loaded, then signal Playwright.
            const waitForTiles = async (attemptsRemaining = 10) => {
                try {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    if (signal.aborted) return;
                    if (tileLayerRef.current && tileLayerRef.current.isLoading()) {
                        if (attemptsRemaining > 1) {
                            return waitForTiles(attemptsRemaining - 1);
                        }
                    }
                    // Extra pause to allow ESRI tiles to finish fading in.
                    await new Promise(resolve => setTimeout(resolve, 500));
                    if (signal.aborted) return;
                    // Wait for header/footer images to finish loading before signalling complete.
                    const outputImgs = [...document.querySelectorAll('.map-render-output img')];
                    let imgWaitMs = 0;
                    while (outputImgs.some(img => !img.complete) && imgWaitMs < 5000) {
                        await new Promise(resolve => setTimeout(resolve, 200));
                        imgWaitMs += 200;
                    }
                    if (signal.aborted) return;
                    document.body.dataset.renderComplete = 'true';
                } catch (err) {
                    if (!signal.aborted) {
                        document.body.dataset.renderError = String(err?.message ?? err ?? 'unknown');
                    }
                }
            };

            waitForTiles();
        }, 1000);

        // Cleanup: cancel the pending timer so Strict Mode's fake unmount/remount doesn't
        // produce a double render. The final real mount re-runs and fires correctly.
        return () => {
            abortController.abort();
            clearTimeout(timerId);
        };
    }, [sketch]);

    return null;
}

function resolveLimitIndex(limits, requestedLimitId, requestedLimitIndex) {
    if (stringUtilities.isNullOrEmpty(requestedLimitId) || arrayUtilities.isNullOrEmpty(limits)) {
        return requestedLimitIndex;
    }

    const matchedIndex = limits.findIndex(x => (x.id ?? '').toLowerCase() === requestedLimitId.toLowerCase());
    return matchedIndex >= 0 ? matchedIndex : requestedLimitIndex;
}

export default function MapRender() {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const tileLayerRef = useRef(null);
    const { api } = useApi();

    const organisationId = params.organisationId;
    const projectId = params.projectId;
    const sketchId = searchParams.get('sketchId');
    const requestedLimitIndex = parseInt(searchParams.get('limitIndex') ?? '0', 10);
    const requestedLimitId = searchParams.get('limitId');
    const workflowId = searchParams.get('workflowId') ?? 'ALL';
    const outputFormat = parseInt(searchParams.get('format') ?? '0', 10);
    const includeDeleted = searchParams.get('includeDeleted') === 'true';
    const surfaceIdsParam = searchParams.get('surfaceIds');
    const selectedSurfaceIds = surfaceIdsParam ? surfaceIdsParam.split(',').filter(Boolean) : null;

    const [project, setProject] = useState(null);
    const [sketch, setSketch] = useState(null);
    const [workflows, setWorkflows] = useState([]);
    const [points, setPoints] = useState(null);
    const [lines, setLines] = useState(null);
    const [workflowPoints, setWorkflowPoints] = useState([]);
    const [workflowLines, setWorkflowLines] = useState([]);
    const [workflowPointsLines, setWorkflowPointsLines] = useState([]);
    const [workflowsReady, setWorkflowsReady] = useState(false);
    const [projectReady, setProjectReady] = useState(false);
    const [basemap, setBasemap] = useState(parseInt(searchParams.get('basemap') ?? '-1', 10));
    const [mapOutputWidth, setMapOutputWidth] = useState(800);
    const [mapOutputHeight, setMapOutputHeight] = useState(450);
    const [snappingTolerance, setSnappingTolerance] = useState(0.5);
    const [arrowScale, setArrowScale] = useState(1);
    const [symbolScale, setSymbolScale] = useState(1);
    const [symbolDensity, setSymbolDensity] = useState(1);
    const [stamps, setStamps] = useState([]);
    const [surfacesData, setSurfacesData] = useState([]);
    const [kmlLayers, setKmlLayers] = useState([]);
    const [ready, setReady] = useState(false);
    const [mapPositioned, setMapPositioned] = useState(false);
    const [mapHeader, setMapHeader] = useState(null);
    const [mapFooter, setMapFooter] = useState(null);

    const limitIndex = resolveLimitIndex(sketch?.limits, requestedLimitId, requestedLimitIndex);

    // Load project and sketch in parallel, then load data once both are available.
    useEffect(() => {
        if (stringUtilities.isNullOrEmpty(organisationId) || stringUtilities.isNullOrEmpty(projectId)) {
            return;
        }

        api.getProject(organisationId, projectId, (projectData) => {
            setProject(projectData);
            if (projectData?.configuration?.mapOutputWidth) {
                setMapOutputWidth(projectData.configuration.mapOutputWidth);
            }
            if (projectData?.configuration?.mapOutputHeight) {
                setMapOutputHeight(projectData.configuration.mapOutputHeight);
            }
            if (projectData?.configuration?.snappingTolerance) {
                setSnappingTolerance(projectData.configuration.snappingTolerance);
            }
            // Query param basemap (from task) takes priority over project default.
            if (basemap === -1 && projectData?.configuration?.mapConfiguration?.basemap != null) {
                setBasemap(projectData.configuration.mapConfiguration.basemap);
            } else if (basemap === -1) {
                setBasemap(0);
            }
            setProjectReady(true);
        });

        api.getSurfaces(organisationId, projectId, (data) => {
            setSurfacesData(Array.isArray(data) ? data : []);
        }, () => {});

        api.getWorkflows(organisationId, projectId, (workflowData) => {
            setWorkflows(Array.isArray(workflowData) ? workflowData : []);
            setWorkflowsReady(true);
        }, () => {
            setWorkflows([]);
            setWorkflowsReady(true);
        });

        api.getStamps(organisationId, (stampData) => {
            setStamps(Array.isArray(stampData) ? stampData : []);
        }, () => {
            setStamps([]);
        });

        api.getOrganisationalUnit(organisationId, (orgData) => {
            if (orgData?.mapHeader && orgData?.mapFooter) {
                setMapHeader(orgData.mapHeader);
                setMapFooter(orgData.mapFooter);
            }
        });

        if (!stringUtilities.isNullOrEmpty(sketchId)) {
            api.getSketch(organisationId, projectId, sketchId, (sketchData) => {
                setSketch(sketchData);
                if (sketchData?.configuration) {
                    if (sketchData.configuration.arrowScale != null) {
                        setArrowScale(sketchData.configuration.arrowScale);
                    }
                    if (sketchData.configuration.symbolScale != null) {
                        setSymbolScale(sketchData.configuration.symbolScale);
                    }
                    if (sketchData.configuration.symbolDensity != null) {
                        setSymbolDensity(sketchData.configuration.symbolDensity);
                    }
                }

                api.getKmlFiles(organisationId, projectId, (kmlFiles) => {
                    const selectedIds = sketchData.configuration?.selectedKmlIds ?? null;
                    const toLoad = selectedIds ? kmlFiles.filter(f => selectedIds.includes(f.id)) : kmlFiles;
                    if (toLoad.length === 0) {
                        return;
                    }
                    const promises = toLoad.map(item =>
                        fetch(item.url)
                            .then(res => res.text())
                            .then(text => {
                                const cleaned = text.indexOf('http://') > -1 ? text.replace(/http:\/\//g, 'https://') : text;
                                const kml = new DOMParser().parseFromString(cleaned, 'text/xml');
                                return { id: item.id, kml };
                            })
                    );
                    Promise.all(promises).then(setKmlLayers).catch(err => console.log(err));
                }, () => {});
            });
        }
    }, [organisationId, projectId, sketchId]);

    useEffect(() => {
        if (!sketch || !workflowsReady) return;
        const cfg = sketch.configuration ?? {};
        const allEmpty = arrayUtilities.isNullOrEmpty(cfg.points) && arrayUtilities.isNullOrEmpty(cfg.lines) && arrayUtilities.isNullOrEmpty(cfg.pointsLines);
        if (allEmpty) {
            const allWorkflowIds = workflows.map(w => w.id);
            setWorkflowPoints(allWorkflowIds);
            setWorkflowLines(allWorkflowIds);
            setWorkflowPointsLines([]);
        } else {
            setWorkflowPoints(cfg.points ?? []);
            setWorkflowLines(cfg.lines ?? []);
            setWorkflowPointsLines(cfg.pointsLines ?? []);
        }
    }, [sketch, workflowsReady]);

    // Load data once sketch is available so we can scope to limit bounds and sketch filters.
    useEffect(() => {
        if (!sketch) {
            return;
        }

        const limit = sketch?.limits?.[limitIndex];
        if (!limit || arrayUtilities.isNullOrEmpty(limit.limitOfSketchCoords)) {
            document.body.dataset.renderEmpty = 'true';
            document.body.dataset.renderComplete = 'true';
            return;
        }

        const coords = limit.limitOfSketchCoords;
        const topLat = Math.max(coords[0].lat, coords[1].lat);
        const bottomLat = Math.min(coords[0].lat, coords[1].lat);
        const leftLng = Math.min(coords[0].lng, coords[1].lng);
        const rightLng = Math.max(coords[0].lng, coords[1].lng);

        const cfg = sketch.configuration ?? {};

        const ALL_ROWS_PAGE_SIZE = 1000000;
        api.getData(
            organisationId, projectId,
            cfg.selectedJobs ?? [], [], [],
            topLat, bottomLat, leftLng, rightLng,
            cfg.dateFrom ?? null, cfg.dateTo ?? null,
            0, 0, null, false, 1, includeDeleted,
            null,
            (data) => {
                const uniqueItems = [...new Map((data.items ?? []).map(p => [p.id, p])).values()];
                setPoints(uniqueItems);
                const lineIds = arrayUtilities.distinct(uniqueItems.filter(p => p.lineId).map(p => p.lineId));
                setLines(lineIds);
                setReady(true);
            },
            () => { setReady(true); },
            ALL_ROWS_PAGE_SIZE
        );
    }, [api, includeDeleted, limitIndex, organisationId, projectId, sketch]);


    const onDimensionsDetected = useCallback((w, h) => {
        setMapOutputWidth(w);
        setMapOutputHeight(h);
    }, []);

    const onMapPositioned = useCallback(() => setMapPositioned(true), []);

    const containerStyle = { width: `${mapOutputWidth}px`, height: `${mapOutputHeight}px` };

    return (
        <div className='map-container d-flex flex-column align-items-center' style={{ width: `${mapOutputWidth}px`, height: `${mapOutputHeight}px` }}>
            <div className='map-render-output' style={{ width: `${mapOutputWidth}px` }}>
                {mapHeader && <img src={mapHeader} alt='' style={{ width: '100%', display: 'block' }} />}
            <div className='map-render-container' style={containerStyle}>
                <MapContainer
                    center={{ lat: 48.166667, lng: -100.166667 }}
                    zoom={4}
                    style={{ width: '100%', height: '100%' }}
                    zoomSnap={0.1}
                    zoomDelta={0.1}
                    attributionControl={false}
                >
                    <BufferOrdering cpmDrawingOrder />
                    <Basemap
                        tileLayerRef={tileLayerRef}
                        basemap={basemap}
                        maxZoom={25}
                    />

                    <Pane name={BUFFER_PANE} style={{ zIndex: BUFFER_PANE_Z_INDEX }} />
                    <Pane name='dimension-line-icons' style={{ zIndex: 5000 }} />
                    <Pane name='drawing-tool-labels' style={{ zIndex: 5001 }} />

                    {kmlLayers.map(layer => (
                        <ReactLeafletKml key={layer.id} kml={layer.kml} />
                    ))}

                    {!arrayUtilities.isNullOrEmpty(surfacesData) && surfacesData.filter(item => selectedSurfaceIds === null || selectedSurfaceIds.includes(item.id?.toString())).map(item => (
                        <Surface key={item.id} surfaceDataObject={item} />
                    ))}

                    {ready && !arrayUtilities.isNullOrEmpty(lines) && lines.map((lineId, index) => (
                        <DataGeomtry
                            key={`line-${lineId}`}
                            index={index}
                            organisationId={organisationId}
                            projectId={projectId}
                            lineId={lineId}
                            points={points}
                            lines={lines}
                            workflows={workflows}
                            workflowLines={workflowLines}
                            sketch={sketch}
                            symbolScale={symbolScale}
                            snappingTolerance={0.5}
                            symbolDensity={symbolDensity}
                            user={{ isGlobalAdmin: false, roleId: -1, capabilityIds: [] }}
                            onSave={() => {}}
                            onSavePointAttributes={() => {}}
                            onMoveData={() => {}}
                            handleDelete={() => {}}
                            handleUnDeleteGeometry={() => {}}
                            handleUnDeletePoint={() => {}}
                        />
                    ))}

                    {ready && !arrayUtilities.isNullOrEmpty(points) && points.map((point, index) => (
                        <DataPoint
                            key={`point-${point.id}`}
                            index={index}
                            cpmDrawingOrder
                            organisationId={organisationId}
                            projectId={projectId}
                            point={point}
                            points={points}
                            lines={lines}
                            workflows={workflows}
                            workflowPoints={workflowPoints}
                            workflowPointsLines={workflowPointsLines}
                            sketch={sketch}
                            symbolScale={symbolScale}
                            snappingTolerance={0.5}
                            user={{ isGlobalAdmin: false, roleId: -1, capabilityIds: [] }}
                            onSave={() => {}}
                            onSavePointAttributes={() => {}}
                            onMoveData={() => {}}
                            handleDelete={() => {}}
                            handleUnDelete={() => {}}
                            handleUnDeleteGeometry={() => {}}
                        />
                    ))}

                    {ready && sketch && mapPositioned && (
                        <Sketch
                            zoom={4}
                            sketch={sketch}
                            onSave={() => {}}
                            project={project}
                            workflows={workflows}
                            points={points}
                            lines={lines}
                            snappingTolerance={0.5}
                            workflowLines={workflowLines}
                            workflowPoints={workflowPoints}
                            workflowPointsLines={workflowPointsLines}
                            arrowScale={arrowScale}
                            symbolScale={symbolScale}
                            symbolDensity={symbolDensity}
                            stamps={stamps}
                            activeLimit={null}
                        />
                    )}

                    {ready && sketch && workflowsReady && projectReady && (
                        <RenderController
                            sketch={sketch}
                            limitIndex={limitIndex}
                            workflowId={workflowId}
                            tileLayerRef={tileLayerRef}
                            mapOutputWidth={mapOutputWidth}
                            mapOutputHeight={mapOutputHeight}
                            outputFormat={outputFormat}
                            workflowLines={workflowLines}
                            workflowPoints={workflowPoints}
                            workflowPointsLines={workflowPointsLines}
                            points={points}
                            lines={lines}
                            snappingTolerance={snappingTolerance}
                            onDimensionsDetected={onDimensionsDetected}
                            onMapPositioned={onMapPositioned}
                        />
                    )}
                </MapContainer>
            </div>
                {mapFooter && <img src={mapFooter} alt='' style={{ width: '100%', display: 'block' }} />}
            </div>
        </div>
    );
}
